import { create } from "zustand";
import type { ProjectSession } from "@/collab/session";
import { deleteFile, readFile, writeFile } from "@/workspace/file-ops";
import { agentDb } from "./agent-db";
import { isSecretPath } from "./tools";

/**
 * Pontos de restauração do agente: antes de o agente criar, mudar ou apagar um arquivo, o conteúdo
 * original é guardado no ponto do pedido atual (um por pedido da pessoa). "Desfazer" devolve todos
 * os arquivos daquele pedido como estavam; cada arquivo também volta sozinho. Desfazer cria outro
 * ponto ("antes de desfazer"), então dá para refazer. Fica no IndexedDB deste computador: o
 * histórico sobrevive a fechar o app. Arquivos de segredo (.env) o agente não lê, então não voltam.
 */

export interface Checkpoint {
  id: string;
  projectId: string;
  at: number;
  /** O pedido da pessoa (resumido), para reconhecer o ponto no histórico. */
  prompt: string;
  /** Conteúdo de antes de cada arquivo tocado; null = o arquivo não existia. */
  files: Record<string, string | null>;
  /** Já foi desfeito (o ponto continua no histórico). */
  undone?: boolean;
}

const STORE = "checkpoints";
/** Pontos guardados por projeto (os mais antigos saem). */
const KEEP = 50;

const tx = <T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>) =>
  agentDb(STORE, mode, fn);

const save = (c: Checkpoint) => tx("readwrite", (s) => s.put(c)).catch(() => undefined);

async function loadProject(projectId: string): Promise<Checkpoint[]> {
  try {
    const all = await tx<Checkpoint[]>("readonly", (s) => s.index("projectId").getAll(projectId));
    const sorted = all.sort((a, b) => b.at - a.at);
    // Limpeza: só os últimos KEEP pontos ficam.
    for (const old of sorted.slice(KEEP))
      void tx("readwrite", (s) => s.delete(old.id)).catch(() => undefined);
    return sorted.slice(0, KEEP);
  } catch {
    return [];
  }
}

/** Projeto cujo histórico está sendo lido agora (várias respostas pedem ao mesmo tempo). */
let loading: string | null = null;

interface CheckpointState {
  projectId: string | null;
  list: Checkpoint[];
  load(projectId: string): Promise<void>;
}

export const useCheckpoints = create<CheckpointState>((set, get) => ({
  projectId: null,
  list: [],
  load: async (projectId) => {
    // Uma leitura por projeto: os pontos novos entram pela memória (upsert), sem reler o banco.
    if (get().projectId === projectId || loading === projectId) return;
    loading = projectId;
    const list = await loadProject(projectId);
    if (loading === projectId) loading = null;
    set({ projectId, list });
  },
}));

const upsert = (c: Checkpoint) => {
  const s = useCheckpoints.getState();
  if (s.projectId !== c.projectId) return;
  const list = [c, ...s.list.filter((x) => x.id !== c.id)].sort((a, b) => b.at - a.at).slice(0, KEEP);
  useCheckpoints.setState({ list });
};

/** O ponto do pedido em andamento (criado no primeiro arquivo que o agente mexer). */
let current: { projectId: string; prompt: string; checkpoint: Checkpoint | null } | null = null;

/** Começa um pedido: as mudanças de arquivo a seguir vão para um ponto novo. */
export function beginCheckpoint(projectId: string, prompt: string) {
  current = { projectId, prompt: prompt.replace(/\s+/g, " ").trim().slice(0, 160), checkpoint: null };
}

/** O ponto do pedido atual (para a resposta mostrar "N arquivos alterados · Desfazer"). */
export const currentCheckpoint = () => current?.checkpoint ?? null;

/**
 * Antes de o agente mexer em `path`: guarda o conteúdo de agora (só da primeira vez no pedido,
 * que é o "antes" de verdade). Chamado logo antes de escrever ou apagar.
 */
export async function rememberOriginal(session: ProjectSession, path: string) {
  if (!current || isSecretPath(path)) return;
  if (!current.checkpoint) {
    current.checkpoint = {
      id: crypto.randomUUID(),
      projectId: current.projectId,
      at: Date.now(),
      prompt: current.prompt,
      files: {},
    };
  }
  const c = current.checkpoint;
  if (path in c.files) return;
  c.files[path] = await readFile(session, path);
  await save(c);
  upsert({ ...c, files: { ...c.files } });
}

/**
 * Volta os arquivos do ponto (todos, ou só `only`) para o conteúdo de antes. Antes, guarda o
 * conteúdo atual num ponto novo ("antes de desfazer"), para dar para refazer.
 */
export async function restoreCheckpoint(
  session: ProjectSession,
  checkpoint: Checkpoint,
  only?: string,
): Promise<{ restored: number; redo: Checkpoint }> {
  const paths = only ? [only] : Object.keys(checkpoint.files);
  const redo: Checkpoint = {
    id: crypto.randomUUID(),
    projectId: checkpoint.projectId,
    at: Date.now(),
    prompt: `Antes de desfazer: ${checkpoint.prompt}`,
    files: {},
  };
  for (const path of paths) redo.files[path] = await readFile(session, path);
  let restored = 0;
  for (const path of paths) {
    const before = checkpoint.files[path];
    if (before === undefined) continue;
    if (before === null) {
      if (deleteFile(session, path)) restored++;
    } else {
      await writeFile(session, path, before);
      restored++;
    }
  }
  await save(redo);
  upsert(redo);
  if (!only) {
    const done = { ...checkpoint, undone: true };
    await save(done);
    upsert(done);
  }
  return { restored, redo };
}
